import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { AuxiaResponse } from "src/pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment";
import { isAuxiaServedPage } from "src/pages/software/jira/service-management/auxia/is-auxia-served-page";
import {
    convertDateTimeToInteractionTimeMicros,
    sendAnalyticsToAuxia
} from "src/pages/software/jira/service-management/auxia/send-analytics-to-auxia";

import { LogoButton } from "@/components/signup/account-creation/components/social/components/logo-button";
import { ProviderLogo } from "@/components/signup/account-creation/components/social/components/provider-logo";
import { useAccountCreationContext } from "@/components/signup/account-creation/hooks/account-creation-context";
import { type SocialProvider } from "@/components/signup/utils/get-social-signup-button-urls";
import { Cookie } from "@/utils/cookie";

export const AuxiaSocial = ({
    providers,
    auxiaResponse
}: {
    providers?: { [K in SocialProvider]?: string };
    auxiaResponse?: AuxiaResponse | null;
}) => {
    const intl = useIntl();
    const { onClickSocialButton } = useAccountCreationContext();

    if (providers === undefined) {
        providers = {
            google: "Google",
            microsoft: "Microsoft",
            apple: "Apple",
            slack: "Slack"
        };
    }

    const buttons = Object.entries(providers);

    const buttonGridCss = css({
        display: "grid",
        gap: "16px",
        gridTemplateColumns: "repeat(2, 1fr)"
    });

    const buttonListCss = css({
        listStyle: "none",
        margin: 0,
        padding: 0
    });

    /* eslint-disable jsx-a11y/no-redundant-roles */
    /* added role="list" to ul for safari https://developer.mozilla.org/en-US/docs/Web/CSS/list-style#accessibility  */
    return (
        <ul
            css={[buttonListCss, buttons.length > 1 && buttonGridCss]}
            role="list"
        >
            {buttons.map(([provider, label]) => (
                <li key={provider}>
                    <LogoButton
                        aria-label={intl.formatMessage(
                            {
                                id: "social.label.signup",
                                defaultMessage: "sign up with {provider}"
                            },
                            {
                                provider: label
                            }
                        )}
                        onClick={() => {
                            if (isAuxiaServedPage(auxiaResponse)) {
                                const anonymousId = decodeURIComponent(
                                    Cookie.readCookie("ajs_anonymous_id") || ""
                                ).replace(/['"]+/g, "");

                                sendAnalyticsToAuxia({
                                    anonymousId: anonymousId,
                                    treatmentTrackingId:
                                        auxiaResponse.treatmentTrackingId,
                                    treatmentId: auxiaResponse.treatmentId,
                                    interactionType: "CLICKED",
                                    interactionTimeMicros:
                                        convertDateTimeToInteractionTimeMicros(
                                            Date.now()
                                        ),
                                    actionName: `${provider}_login`
                                });
                            }

                            return onClickSocialButton(provider);
                        }}
                    >
                        <ProviderLogo logo={provider} />
                        <span>{label}</span>
                    </LogoButton>
                </li>
            ))}
        </ul>
    );
};
